/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import { ThunkAction } from 'model/store/thunk'
import RequestAction from 'store/RequestAction'
import { EmailTemplate } from 'types/entities/EmailTemplate'
import { Offer } from 'types/entities/Offer'
import { deleteOfferEmailTemplate, pushOfferEmailTemplate, setOfferEmailTemplates } from './OffersReducer'
import { EmailExportUtils } from 'utils/email/EmailExportUtils'
import { getCompanyDisplayName } from 'utils/companyUtils'
import {currencyNumberFormat} from 'utils/utility'
import {getOfferPrimaryPackagesCart} from 'utils/offers/offersUtils'

interface ApiTemplate {
    id: number
    name: string
    user_id: number
    data: {
        subject: string
        content: string
        model_id: string
    }
    created_at: string | null
    updated_at: string | null
}

export class OfferEmailTemplatesAction {
    static fetchCreateOrUpdateTemplate = (
        data: Partial<EmailTemplate>,
        files?: File[]
    ): ThunkAction<Promise<EmailTemplate>> => {
        return async (dispatch, getState): Promise<EmailTemplate> => {
            const state = getState()
            const creatorId = state.sessionState.session?.user?.id

            const formData = new FormData()
            if (data.id) formData.append('id', String(data.id))
            formData.append('name', data.name || 'Email Template')
            formData.append('type', 'mail')
            if (creatorId) formData.append('creator_id', String(creatorId))

            // Append specific data fields for model association
            formData.append('data[model_name]', 'Offer')
            formData.append('data[model_id]', String(data.model_id))
            formData.append('data[subject]', data.subject || '')
            formData.append('data[content]', data.content || '')

            // Append files to be sent as part of multipart/form-data
            if (files && files.length > 0) {
                files.forEach((file) => {
                    formData.append('files[]', file)
                })
            }

            const response = await RequestAction.makeAuthenticatedRequest<{importMailTemplate?: ApiTemplate}>(
                getState,
                '/admin/import/create-mail-template',
                formData,
                'post'
            )

            if (response && response.importMailTemplate) {
                const t = response.importMailTemplate
                const mappedTemplate: EmailTemplate = {
                    id: t.id,
                    model_id: Number(t.data.model_id),
                    model_name: 'Offer',
                    name: t.name,
                    subject: t.data.subject,
                    content: t.data.content,
                    created_at: t.created_at || undefined,
                    updated_at: t.updated_at || undefined,
                }
                dispatch(pushOfferEmailTemplate(mappedTemplate))
                return mappedTemplate
            }

            return Promise.reject(response)
        }
    }

    static fetchDeleteTemplate = (
        template: EmailTemplate
    ): ThunkAction<Promise<void>> => {
        return async (dispatch, getState): Promise<void> => {
            try {
                await RequestAction.makeAuthenticatedRequest<unknown>(
                    getState,
                    `/admin/offers/email-templates/delete/${template.id}`,
                    undefined,
                    'delete'
                )
                // If we get here, the request succeeded (AxiosCommon would have rejected on error)
                dispatch(deleteOfferEmailTemplate(template))
                // Refresh the list and wait for it to complete
                await dispatch(OfferEmailTemplatesAction.fetchTemplates())
            } catch (error) {
                return Promise.reject(error)
            }
        }
    }

    static fetchTemplates = (): ThunkAction<Promise<EmailTemplate[]>> => {
        return async (dispatch, getState): Promise<EmailTemplate[]> => {

            interface ApiTemplateData {
                importMailTemplates: ApiTemplate[]
            }

            const response = await RequestAction.makeAuthenticatedRequest<
                ApiTemplateData
            >(getState, '/admin/offers/email-templates/list', undefined, 'get')

            if (response && response.importMailTemplates) {
                const mappedTemplates: EmailTemplate[] =
                    response.importMailTemplates.map((t) => ({
                        id: t.id,
                        model_id: Number(t.data.model_id),
                        model_name: 'Offer',
                        name: t.name,
                        subject: t.data.subject,
                        content: t.data.content,
                        created_at: t.created_at || undefined,
                        updated_at: t.updated_at || undefined,
                    }))

                dispatch(setOfferEmailTemplates(mappedTemplates))
                return mappedTemplates
            }

            return Promise.reject(response)
        }
    }

    static exportToEml = async (
        template: EmailTemplate,
        offer?: Offer | null,
        attachments: File[] = []
    ) => {
        const replacements: Record<string, string> = {}
        if (offer) {
            const company = offer.company
            const defaultContact = company?.contacts?.find(
                (c) => c.id === company.default_contact_id
            )
            const totalPrice = getOfferPrimaryPackagesCart(offer)?.price

            replacements['{{offer_guid}}'] = offer.guid || ''
            replacements['{{offer_status}}'] = offer.status || ''
            replacements['{{offer_valid_until}}'] = offer.valid_until || ''
            replacements['{{offer_total_price}}'] = totalPrice == null ? '' : currencyNumberFormat(totalPrice)
            replacements['{{company_name}}'] = getCompanyDisplayName(company)
            replacements['{{contact_full_name}}'] = defaultContact ? `${defaultContact.first_name} ${defaultContact.last_name}` : ''
        }

        await EmailExportUtils.exportToEml(template, replacements, attachments)
    }
}
